import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import Logo from "./Logo";
import { GULF_NAV } from "../lib/gulfConstants";
import { trackCtaClick } from "../lib/api";

/** Gulf site header — appears on / and every Gulf sub-page. Same UX as
 *  EgHeaderNav but uses GULF_NAV paths and "nav-gulf-*" analytics labels. */
export default function GulfHeaderNav({ variant = "absolute" }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const go = (to, testid) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen(false);
    trackCtaClick(testid);
    if (location.pathname !== to) {
      navigate(to);
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const positioning =
    variant === "static"
      ? "relative bg-[#0B1D3A]/80 border-b border-[#C5A059]/15"
      : "absolute top-0 inset-x-0 z-30";

  return (
    <header className={`${positioning} px-5 sm:px-10 py-4 sm:py-5`} data-testid="gulf-header-nav">
      <div className="max-w-6xl mx-auto flex items-center justify-between gap-4">
        <Link
          to="/"
          className="shrink-0"
          data-testid="gulf-header-logo"
          onClick={go("/", "nav-gulf-home")}
        >
          <Logo className="h-10 w-10 sm:h-11 sm:w-11" />
        </Link>

        <nav className="hidden md:flex items-center gap-7 text-sm" dir="rtl">
          {GULF_NAV.map((item) => {
            const active = location.pathname === item.to;
            return (
              <Link
                key={item.to}
                to={item.to}
                onClick={go(item.to, item.testid)}
                data-testid={item.testid}
                className={`transition-colors ${
                  active ? "text-[#C5A059] font-bold" : "text-white/85 hover:text-[#C5A059]"
                }`}
              >
                {item.label}
              </Link>
            );
          })}
        </nav>

        <button
          onClick={() => setOpen((v) => !v)}
          className="md:hidden text-[#C5A059] p-2 -mr-2"
          aria-label="القائمة"
          data-testid="gulf-mobile-menu-btn"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <>
          <button
            type="button"
            aria-label="إغلاق القائمة"
            onClick={() => setOpen(false)}
            className="md:hidden fixed inset-0 z-40 bg-[#040914]/70 backdrop-blur-sm cursor-default"
            data-testid="gulf-mobile-menu-backdrop"
          />
          <div
            className="md:hidden absolute top-full inset-x-0 z-50 bg-[#040914] border-t border-[#C5A059]/20 shadow-2xl shadow-black/60"
            dir="rtl"
            data-testid="gulf-mobile-menu"
          >
            <nav className="flex flex-col px-6 py-4">
              {GULF_NAV.map((item) => {
                const active = location.pathname === item.to;
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    onClick={go(item.to, item.testid)}
                    data-testid={`${item.testid}-mobile`}
                    className={`py-3 text-base border-b border-[#C5A059]/10 last:border-0 ${
                      active ? "text-[#C5A059] font-bold" : "text-white/90"
                    }`}
                  >
                    {item.label}
                  </Link>
                );
              })}
            </nav>
          </div>
        </>
      )}
    </header>
  );
}
